import { ref, uploadBytes } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { storage } from "/firebaseConfig";

/**
 * Toast UI Editor `addImageBlobHook`용 업로드 헬퍼.
 * Firebase Storage의 `toastui/` 경로에 업로드 후 공개 다운로드 URL을 반환합니다.
 *
 * @param {File|Blob} blob - 에디터에서 전달된 이미지 Blob
 * @param {string} [folder="toastui"] - Storage 하위 폴더(기본: toastui)
 * @returns {Promise<string>} 업로드된 이미지의 다운로드 URL
 */
export async function uploadToastImage(blob, folder = "toastui") {
  if (!blob) throw new Error("업로드할 이미지가 없습니다.");

  const rawName = blob.name || "image";
  const extFromName = rawName.includes(".") ? rawName.split(".").pop().toLowerCase() : "";
  const extFromType = (blob.type && blob.type.includes("/"))
    ? blob.type.split("/").pop().toLowerCase()
    : "";
  const ext = (extFromName || extFromType || "png").replace(/[^a-z0-9]/g, "").slice(0, 5) || "png";

  const timestamp = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  const path = `${folder}/${timestamp}_${rand}.${ext}`;

  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, blob.type ? { contentType: blob.type } : undefined);
  return await getDownloadURL(storageRef);
}

/**
 * Toast UI Editor / Viewer에 넘기기 전 마크다운 정리.
 *
 * Toast UI Editor 3.x WYSIWYG가 다음 케이스를 헤딩으로 인식하지 못해
 * 화면에 `#`이 그대로 보이는 문제를 보정한다.
 *
 * - 줄 시작의 `\#\#\# ...` → `### ...` (이스케이프 해제 후 공백 보장)
 * - 줄 시작의 `###제목`     → `### 제목` (`#` 뒤 공백 보장)
 * - 줄 시작의 `> ### 제목`  → `### 제목` (블록인용 안의 헤딩을 바깥으로 끌어올림)
 * - 펜스 코드 블록(```) 안 줄은 건드리지 않음
 *
 * 같은 함수를 Editor 로드 시점과 Viewer 마운트 시점에 동일하게 적용해
 * 둘이 동일한 입력으로 동작하도록 한다.
 *
 * @param {string} md
 * @returns {string}
 */
export function prepareToastViewerMarkdown(md) {
  if (!md || typeof md !== "string") return md;
  const lines = md.split("\n");
  let inFence = false;
  const out = [];
  for (const line of lines) {
    const t = line.trimStart();
    if (t.startsWith("```")) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }

    const blockquoteHeading = line.match(/^(\s{0,3})>\s*(#{1,6})(.*)$/);
    if (blockquoteHeading) {
      const indent = blockquoteHeading[1];
      const hashes = blockquoteHeading[2];
      const rest = blockquoteHeading[3];
      const sep = rest === "" || rest.startsWith(" ") ? "" : " ";
      out.push(`${indent}${hashes}${sep}${rest}`);
      continue;
    }

    const escaped = line.match(/^(\s{0,3})((?:\\#){1,6})(.*)$/);
    if (escaped) {
      const indent = escaped[1];
      const hashCount = escaped[2].length / 2;
      const rest = escaped[3];
      const sep = rest === "" || rest.startsWith(" ") ? "" : " ";
      out.push(`${indent}${"#".repeat(hashCount)}${sep}${rest}`);
      continue;
    }

    const tight = line.match(/^(\s{0,3})(#{1,6})([^\s#])/);
    if (tight) {
      out.push(`${tight[1]}${tight[2]} ${tight[3]}${line.slice(tight[0].length)}`);
      continue;
    }

    out.push(line);
  }
  return out.join("\n");
}
